import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import classNames from "classnames";
import moment from "moment";

import { useStateSelector } from "utils/field";
import { getSortableName } from "utils/directory/sorting";
import {
    getUpgradeEnvelopeRowLabels,
    getUpgradeHvacRowLabels,
    getUpgradeDhwRowLabels,
    getUpgradeOtherRowLabels,
    envelopeRowHeadings,
    hvacRowHeadings,
    dhwRowHeadings,
    otherRowHeadings,
    communityFinancingRowHeadings,
    getUpgradeFinancingRowsLabels,
    getInitAirtightUpgradeFields,
    dollarFormatter,
    getBlankUpgradeTemplates,
    getSubComponentOptions,
    getComponentOptions,
    stripWindowSkylights,
    stripCeilings,
    getInitComponentUpgradeFields,
    getBlankComponentUpgradeFields,
} from "utils/upgrades";
import { extractCodesFromCodeLib } from "utils/codes";

import UpgradeDrawer from "features/Model/Upgrades/UpgradeDrawer";
import PackageEditDrawer from "features/Model/Upgrades/PackageEditDrawer";
import CostInput from "features/Model/Upgrades/UpgradesTable/CostInput/CostInput";
import CodeDrawer from "features/Model/CodeDrawer";

import Loading from "components/Loading";
import TableWithFrozenHeader from "components/TableWithFrozenHeader";
import Tooltip from "components/Tooltip";
import CommUpgradeHead from "./UpgradeHead";
import CommUpgradeHeadBase from "./UpgradeHeadBase";
// import UpgradeTableSimulations from "./UpgradeTableSimulations/UpgradeTableSimulations";
import RowToggle from "./RowToggle/RowToggle";

import { removeUpgradeFromCommunity } from "store/community/actions";
import { fetchUpgradePackage } from "store/upgradeLibrary/thunk";
import {
    selectCommunityLoading,
    selectCommunityModelData,
    selectCommunityModelDir,
    selectUpgradePackagesInRun,
} from "store/community/selectors";
import { selectCapitalCosts, selectUpgradePackagesSortedArray } from "../_ducks/selectors";
import { actions as upgradeActions } from "../_ducks";
import { actions as commUpgradeActions } from "features/Community/Upgrades/_ducks";

import Add from "assets/images/icons/JSX/Add";
import Edit from "assets/images/icons/JSX/Edit";
import WarningIcon from "assets/images/icons/WarningOrange.svg";

import classes from "./style.module.scss";
import sharedClasses from "../../style.module.scss";
import mainClasses from "hoc/Layout/style.module.scss";

const { setCommBaseModelId } = upgradeActions;
const { updateCommUpgradePackage } = commUpgradeActions;

const buildRow = ({ id, value, content, hideRows, hiddenRows = {}, columns = {}, className, change }) => {
    return {
        id,
        house: {
            value,
            content: (
                <>
                    <strong>{content}</strong>{" "}
                    <RowToggle
                        className={classNames(classes.rowToggle, {
                            [classes.hide]: !hiddenRows[id],
                        })}
                        rowId={id}
                        change={change}
                    />
                </>
            ),
            className,
            // verticalAlign: "top",
        },
        rowClassName: hideRows && hiddenRows[id] ? classes.hiddenRow : "",
        ...columns,
    };
};

const baseDefault = {
    value: "base",
    content: "Base Value",
    upgradeSet: true,
    className: `${classes.regularCell} ${classes.baseCell}`,
};

const emptyDividerCell = {
    value: "",
    className: classes.dividerCell,
};

export const getEmptyDividerCells = (upgrades, showCost) => {
    return {
        base: emptyDividerCell,
        ...upgrades.reduce(
            (obj, { id }) => ({
                ...obj,
                [id]: emptyDividerCell,
                ...(showCost ? { [`${id}-cost`]: emptyDividerCell } : {}),
            }),
            {}
        ),
    };
};

const buildUpgradeCell = ({
    empty,
    componentsRemaining,
    toggleUpgrade,
    upgrades = [],
    showCost,
    setUpgradeWhereToAddCode,
    filteredUpgrades,
    canBeAddedFromCommunity = true,
}) => {
    return {
        content: upgrades.map(
            ({ displayLabel, upgradeId, cost, onChangeCost, upgradeType, hasCost, showWarning }, ind) => {
                const upgradesPackages = filteredUpgrades[upgradeType] || {};
                let isTotalCost = ["totalCapitalCost", "totalCapitalCostSelectedBase"].includes(upgradeType);

                //Create a new one if it doesn't exist yet
                var newUpgradeId = upgradeId ? upgradeId : `upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${0}`;

                return (
                    <span key={`upgrade-${upgradeId}`} className={classes.upgradeCellText}>
                        <span className={classes.upgradeCellTextLabel}>
                            {showWarning && (
                                <span
                                    style={{ width: "max-content", height: "max-content" }}
                                    data-for={`upgrade-table-tooltip-${upgradeId}`}
                                    data-html
                                    data-tip={`<span style="display:block;max-width:14rem;">An error occurred trying to locate the selected code data. Please re-select the desired code from the drop-down list and save your changes.</span>`}
                                >
                                    <img src={WarningIcon} alt="Warning" />
                                </span>
                            )}
                            {empty && !isTotalCost ? "-" : displayLabel}
                        </span>{" "}
                        {!empty && !isTotalCost && (
                            <span
                                data-for={`upgrade-table-tooltip-${upgradeId}`}
                                data-tip="Edit Upgrade"
                                data-place="left"
                                onClick={() => {
                                    const whereToAddCode = {
                                        ecmInputMapUpgradeType: upgradeType,
                                        ecmInputMapUpgradeId: upgradeId,
                                        ecmInputMapUpgrade: upgradesPackages[upgradeId]?.packageValues || {},
                                        ecmInputMapLocation: "community",
                                    };
                                    setUpgradeWhereToAddCode(whereToAddCode);
                                    toggleUpgrade(upgradeId, whereToAddCode, false);
                                }}
                                className={classes.edit}
                            >
                                <Edit />
                            </span>
                        )}
                        {(empty || componentsRemaining) && !isTotalCost && canBeAddedFromCommunity && (
                            <span
                                data-for={`upgrade-table-tooltip-${newUpgradeId}`}
                                data-tip="Add Upgrade"
                                data-place="left"
                                onClick={() => {
                                    const whereToAddCode = {
                                        ecmInputMapUpgradeType: upgradeType,
                                        ecmInputMapUpgradeId: newUpgradeId,
                                        ecmInputMapUpgrade: {},
                                        ecmInputMapLocation: "community",
                                    };
                                    setUpgradeWhereToAddCode(whereToAddCode);
                                    toggleUpgrade(newUpgradeId, whereToAddCode, true);
                                }}
                                className={classes.edit}
                            >
                                <Add />
                            </span>
                        )}
                        {showCost && !isTotalCost && (
                            <span className={classes.upgradeCellCostText}>
                                {empty ? (
                                    "-"
                                ) : (
                                    <>
                                        <span>$</span> {/* add hasCost as prop here*/}
                                        <CostInput
                                            hasCost={true}
                                            setValue={cost}
                                            value={cost ?? 0}
                                            onChange={onChangeCost}
                                        />
                                    </>
                                )}
                            </span>
                        )}
                        <Tooltip id={`upgrade-table-tooltip-${upgradeId}`} />
                    </span>
                );
            }
        ),
        className: classNames(`${classes.regularCell} ${classes.upgradeCell}`, {
            [classes.fadeCell]: empty,
            [classes.cellWithCost]: showCost,
        }),
    };
};

const CommunitiesUpgradesTable = React.forwardRef(
    (
        {
            // parent props
            accessor,
            change,
            uid,
            showCost,
            toggleShowCost,
            toggleToDelete,
            duplicateUpgrade,
            ecmInputMap,
            missingCodes,
            // state props
            modelDir,
            modelData: { models: modelData },
            communityLoading,
            capitalCosts,
            upgradePackages = [],
            upgradePackagesInRun,
            // dispatch props
            fetchUpgradePackage,
            removeUpgradeFromCommunity,
            setCommBaseModelId,
            updateCommUpgradePackage,
        },
        ref
    ) => {
        const topScrollbarRef = useRef(null);
        const lockedHeaderRef = useRef(null);
        const tableContainerRef = ref;

        const [scrolledRight, setScrolledRight] = useState(false);
        const [scrolledDown, setScrolledDown] = useState(false);

        const [upgradeOpen, toggleUpgradeDrawer] = useState({});
        const [packageEditOpen, togglePackageEdit] = useState({});

        const [upgradeWhereToAddCode, setUpgradeWhereToAddCode] = useState({});

        const { models = {} } = modelDir;

        const commModels = Object.fromEntries(
            Object.entries(models).sort(([aKey, aValues], [bKey, bValues]) =>
                getSortableName(aValues?.name).localeCompare(getSortableName(bValues?.name))
            )
        );

        const filteredUpgrades = Object.fromEntries(
            Object.entries(ecmInputMap).filter(([_, upgradesPackages]) => Object.keys(upgradesPackages).length > 1)
        );

        const originalId = Object.keys(commModels)[0] || null;
        const modelsDefaultCodes = Object.entries(modelData).map(([key, value]) => [
            key,
            value.defaultCodes ? value.defaultCodes : null,
        ]);

        const [baseModelId, setBaseModelId] = useState(originalId);
        const [baseModelData, setBaseModelData] = useState(modelData[originalId]);
        const [baseModelDetails, setBaseModelDetails] = useState(models[originalId]);

        useEffect(() => {
            setCommBaseModelId(baseModelId);
            setBaseModelData(modelData[baseModelId]);
            setBaseModelDetails(models[baseModelId]);
        }, [baseModelId]);

        const { upgrades: { hideArchived = false, hideRows = false, hiddenRows = {} } = {} } =
            useStateSelector(`${accessor}`) || {};

        const rowFilter = ({ id }) => id !== "transportation";

        const filteredUpgradePackages = hideArchived
            ? upgradePackages.filter(({ archived }) => !archived)
            : upgradePackages;

        const handleUpgradeCostChange = () => {};

        const upgradeWarningList = missingCodes.map(([codeRef, { upgradeId }]) => upgradeId);

        /* Sync scroll between the table content and frozen header */
        useEffect(() => {
            const tableWrapper = ref?.current;
            const topScrollbar = topScrollbarRef?.current;
            const lockedHeader = lockedHeaderRef?.current;

            const syncScroll = (source, target1, target2) => {
                if (target1) {
                    target1.scrollLeft = source.scrollLeft;
                }
                if (target2) {
                    target2.scrollLeft = source.scrollLeft;
                }
            };

            topScrollbar?.addEventListener("scroll", () => syncScroll(topScrollbar, tableWrapper, lockedHeader));
            lockedHeader?.addEventListener("scroll", () => syncScroll(lockedHeader, topScrollbar, tableWrapper));
            tableWrapper?.addEventListener("scroll", () => syncScroll(tableWrapper, lockedHeader, topScrollbar));

            return () => {
                topScrollbar?.removeEventListener("scroll", () => syncScroll(topScrollbar, tableWrapper, lockedHeader));
                lockedHeader?.removeEventListener("scroll", () => syncScroll(lockedHeader, topScrollbar, tableWrapper));
                tableWrapper?.removeEventListener("scroll", () => syncScroll(tableWrapper, lockedHeader, topScrollbar));
            };
        }, [ref]);

        // Check if the table is scrolled down and apply the scrolledDown class
        useEffect(() => {
            // Use scrollbar from main container
            const main = document.getElementsByClassName(mainClasses.main);

            const initiateScrollDown = () => {
                const el = lockedHeaderRef?.current;
                if (!el) return;

                const rect = el.getBoundingClientRect();
                const topThreshold = 76;

                // The sticky header is at or above the top of the viewport
                if (rect.top.toFixed(0) <= topThreshold) {
                    setScrolledDown(true);
                } else {
                    setScrolledDown(false);
                }
            };
            main[0]?.addEventListener("scroll", initiateScrollDown);

            return () => {
                main[0]?.removeEventListener("scroll", initiateScrollDown);
            };
        }, [ref]);

        /* Check if the table is scrolled horizontally and apply the scrolledRight class */
        useEffect(() => {
            const tableWrapper = ref?.current;

            const initiateScrollRight = () => {
                if (tableWrapper?.scrollLeft > 0) {
                    setScrolledRight(true);
                } else {
                    setScrolledRight(false);
                }
            };

            tableWrapper?.addEventListener("scroll", initiateScrollRight);

            return () => {
                tableWrapper?.removeEventListener("scroll", initiateScrollRight);
            };
        }, [ref]);

        return (
            <div
                className={classNames(classes.upgradesTableWrapper, {
                    [classes.scrolledRight]: scrolledRight,
                    [classes.scrolledDown]: scrolledDown,
                })}
            >
                {!communityLoading ? (
                    <>
                        <TableWithFrozenHeader
                            tableRef={tableContainerRef}
                            topScrollbarRef={topScrollbarRef}
                            lockedHeaderRef={lockedHeaderRef}
                            className={classes.upgradesTable}
                            headClassName={classes.upgradesTableHead}
                            isCommunityUpgradesTable={true}
                            headCells={[
                                {
                                    id: "house",
                                    label: "House Components",
                                    align: "left",
                                    className: `${classes.headCell} ${classes.freezeCell}`,
                                    width: "290",
                                },
                                {
                                    id: "base",
                                    label: (
                                        <CommUpgradeHeadBase
                                            name={baseModelDetails?.name || "Base Model"}
                                            currentModelId={baseModelId}
                                            packageId="base"
                                            handleSelectBaseModel={(modelId) => {
                                                setBaseModelId(modelId);
                                            }}
                                            modelDir={{ models: commModels }}
                                            uid={uid}
                                        />
                                    ),
                                    align: "left",
                                    className: `${classes.headCell} ${classes.upgradeCell} ${classes.baseCell}`,
                                    width: "268",
                                },
                                ...filteredUpgradePackages.reduce(
                                    (arr, { id, name, upgrades = {}, archived = false }) => {
                                        const packageCodes = Object.values(upgrades).reduce(
                                            (acc, { codes }) => ({ ...acc, ...codes }),
                                            {}
                                        );

                                        return [
                                            ...arr,
                                            {
                                                id,
                                                label: (
                                                    <CommUpgradeHead
                                                        name={name}
                                                        packageId={id}
                                                        packageCount={upgradePackages.length}
                                                        packageIds={upgradePackages.map(({ id }) => id)}
                                                        togglePackageEdit={() => {
                                                            togglePackageEdit({
                                                                isOpen: true,
                                                                selectedPackage: id,
                                                                modelsDefaultCodes,
                                                            });
                                                        }}
                                                        duplicateUpgrade={duplicateUpgrade}
                                                        deletePackage={() => {
                                                            toggleToDelete(id);
                                                        }}
                                                        archivePackage={async (value) => {
                                                            updateCommUpgradePackage({
                                                                packageId: id,
                                                                packageUpdates: {
                                                                    archived: value,
                                                                },
                                                            });
                                                        }}
                                                        showCost={showCost}
                                                        uid={uid}
                                                        codes={packageCodes}
                                                        fetchLabels={(uid, orgId) => fetchUpgradePackage(uid, orgId)} // check
                                                        isInRun={!!upgradePackagesInRun?.[id]}
                                                        isArchived={archived}
                                                        modelsDefaultCodes={modelsDefaultCodes}
                                                    />
                                                ),
                                                align: "left",
                                                className: `${classes.headCell} ${classes.upgradeCell}`,
                                                width: showCost ? "380" : "268",
                                            },
                                        ];
                                    },
                                    []
                                ),
                            ]}
                            rows={[
                                {
                                    house: {
                                        id: "envelope",
                                        value: "envelope",
                                        content: (
                                            <div>
                                                <strong>ENVELOPE</strong>
                                            </div>
                                        ),
                                        className: `${classes.dividerCell} ${classes.freezeCell}`,
                                    },
                                    ...getEmptyDividerCells(upgradePackages, showCost),
                                },
                                ...envelopeRowHeadings.filter(rowFilter).map((obj) =>
                                    buildRow({
                                        ...obj,
                                        change,
                                        hideRows,
                                        hiddenRows,
                                        columns: {
                                            // this is where pre-retrofit data will be applied
                                            base: {
                                                ...baseDefault,
                                                content: baseModelId
                                                    ? getUpgradeEnvelopeRowLabels({
                                                          components: baseModelData?.components || {},
                                                          codes: baseModelData?.codes || {},
                                                          airtightness: baseModelData?.airTightness || {},
                                                          rowId: obj.id,
                                                      })?.label
                                                    : "-",
                                                className: `${classes.regularCell} ${classes.baseCell}`,
                                                // verticalAlign: "top",
                                            },
                                            ...upgradePackages.reduce((packagesObj, upgradePackage) => {
                                                const { id, upgrades = {} } = upgradePackage;
                                                const isInRun = !!upgradePackagesInRun?.[id];
                                                const { label = "", upgradeIds = [] } = getUpgradeEnvelopeRowLabels({
                                                    components: {},
                                                    codes: {},
                                                    airtightness: {},
                                                    rowId: obj.id,
                                                    upgradePackage: {
                                                        [id]: upgradePackage,
                                                    },
                                                }) || {};

                                                let initUpgradeData = getBlankComponentUpgradeFields(obj.id, true);

                                                const toggleUpgrade = (upgradeId, whereToAddCode, isNew) => {
                                                    return toggleUpgradeDrawer({
                                                        isOpen: true,
                                                        formName: "upgrades",
                                                        selectedPackage: id,
                                                        selectedUpgrade: upgradeId,
                                                        selectedUpgradeType: obj.id,
                                                        selectedUpgradeH2kType: obj.upgradeType,
                                                        selectedUpgradeTypeLabel: obj.content,
                                                        selectedUpgradeLabel: label || "None",
                                                        initUpgradeData,
                                                        isNew: isNew,
                                                        upgradeWhereToAddCode: whereToAddCode,
                                                        canBeEditedFromCommunity: !isInRun,
                                                        resetState: () => {
                                                            setUpgradeWhereToAddCode({});
                                                            removeUpgradeFromCommunity(obj.id, upgradeId);
                                                        },
                                                        modelsDefaultCodes,
                                                    });
                                                };

                                                const upgradesObj =
                                                    upgradeIds.length === 0
                                                        ? buildUpgradeCell({
                                                              empty: true,
                                                              toggleUpgrade,
                                                              upgrades: [
                                                                  {
                                                                      displayLabel: label,
                                                                      upgradeType: obj.id,
                                                                  },
                                                              ],
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                              canBeAddedFromCommunity: !isInRun,
                                                          })
                                                        : buildUpgradeCell({
                                                              empty: false,
                                                              toggleUpgrade,
                                                              componentsRemaining: false,
                                                              upgrades: upgradeIds.map((upgradeId) => {
                                                                  return {
                                                                      displayLabel:
                                                                          upgrades[upgradeId]?.label?.[obj.id] || label,
                                                                      upgradeType: obj.id,
                                                                      upgradeId,
                                                                      showWarning:
                                                                          upgradeWarningList.includes(upgradeId),
                                                                      //   hasCost,
                                                                      //   pulling cost from the cost table
                                                                      cost:
                                                                          upgrades[upgradeId]?.cost?.[obj.id]
                                                                              ?.communityTotals?.[baseModelId] || 0,
                                                                      onChangeCost: (cost) =>
                                                                          handleUpgradeCostChange({
                                                                              packageId: id,
                                                                              upgradeId,
                                                                              upgradeType: obj.id,
                                                                              cost,
                                                                          }),
                                                                  };
                                                              }),
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                          });

                                                return {
                                                    ...packagesObj,
                                                    [id]: upgradesObj,
                                                };
                                            }, {}),
                                        },
                                        className: `${classes.regularCell} ${classes.freezeCell} ${classes.flexCell}`,
                                    })
                                ),
                                {
                                    house: {
                                        id: "hvac",
                                        value: "hvac",
                                        content: <strong>HVAC</strong>,
                                        className: `${classes.dividerCell} ${classes.freezeCell}`,
                                    },
                                    ...getEmptyDividerCells(upgradePackages, showCost),
                                },
                                ...hvacRowHeadings.filter(rowFilter).map((obj) =>
                                    buildRow({
                                        ...obj,
                                        change,
                                        hideRows,
                                        hiddenRows,
                                        columns: {
                                            base: {
                                                ...baseDefault,
                                                content: baseModelId
                                                    ? getUpgradeHvacRowLabels({
                                                          heatingCooling: baseModelData?.heatingCooling || {},
                                                          ventilation: baseModelData?.ventilation || {},
                                                          rowId: obj.id,
                                                      })?.label
                                                    : "-",
                                                className: `${classes.regularCell} ${classes.baseCell}`,
                                                // verticalAlign: "top",
                                            },
                                            ...upgradePackages.reduce((packagesObj, upgradePackage) => {
                                                const { id, upgrades = {}, upgradeSettings = {} } = upgradePackage;
                                                const isInRun = !!upgradePackagesInRun?.[id];
                                                const { label = "", upgradeIds = [] } = getUpgradeHvacRowLabels({
                                                    heatingCooling: {},
                                                    ventilation: {},
                                                    rowId: obj.id,
                                                    upgradePackage: {
                                                        [id]: upgradePackage,
                                                    },
                                                }) || {};

                                                const initUpgradeData = {
                                                    fields: getBlankUpgradeTemplates(obj.id),
                                                };

                                                const toggleUpgrade = (upgradeId, whereToAddCode, isNew) =>
                                                    toggleUpgradeDrawer({
                                                        isOpen: true,
                                                        formName: "upgrades",
                                                        selectedPackage: id,
                                                        selectedUpgrade: upgradeId,
                                                        selectedUpgradeType: obj.id,
                                                        selectedUpgradeH2kType: obj.upgradeType,
                                                        selectedUpgradeTypeLabel: obj.content,
                                                        selectedUpgradeLabel: label || "None",
                                                        initUpgradeData,
                                                        isNew: isNew,
                                                        upgradeWhereToAddCode: whereToAddCode,
                                                        canBeEditedFromCommunity: !isInRun,
                                                        resetState: () => {
                                                            setUpgradeWhereToAddCode({});
                                                            removeUpgradeFromCommunity(obj.id, upgradeId);
                                                        },
                                                        modelsDefaultCodes,
                                                    });

                                                const upgradesObj =
                                                    upgradeIds.length === 0
                                                        ? buildUpgradeCell({
                                                              empty: true,
                                                              toggleUpgrade,
                                                              upgrades: [
                                                                  {
                                                                      displayLabel: label,
                                                                      upgradeType: obj.id,
                                                                  },
                                                              ],
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                              canBeAddedFromCommunity: !isInRun,
                                                          })
                                                        : buildUpgradeCell({
                                                              empty: false,
                                                              toggleUpgrade,
                                                              componentsRemaining: false,
                                                              upgrades: upgradeIds.map((upgradeId) => {
                                                                  return {
                                                                      displayLabel:
                                                                          upgrades[upgradeId]?.label?.[obj.id] || label,
                                                                      upgradeType: obj.id,
                                                                      upgradeId,
                                                                      cost:
                                                                          upgrades[upgradeId]?.cost?.[obj.id]
                                                                              ?.communityTotals?.[baseModelId] || 0,
                                                                      onChangeCost: (cost) =>
                                                                          handleUpgradeCostChange({
                                                                              packageId: id,
                                                                              upgradeId,
                                                                              upgradeType: obj.id,
                                                                              cost,
                                                                          }),
                                                                  };
                                                              }),
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                          });

                                                return {
                                                    ...packagesObj,
                                                    [id]: upgradesObj,
                                                };
                                            }, {}),
                                        },
                                        className: `${classes.regularCell} ${classes.freezeCell} ${classes.flexCell}`,
                                    })
                                ),
                                {
                                    house: {
                                        id: "dhw",
                                        value: "dhw",
                                        content: <strong>DHW</strong>,
                                        className: `${classes.dividerCell} ${classes.freezeCell}`,
                                    },
                                    ...getEmptyDividerCells(upgradePackages, showCost),
                                },
                                ...dhwRowHeadings.filter(rowFilter).map((obj) => {
                                    return buildRow({
                                        ...obj,
                                        change,
                                        hideRows,
                                        hiddenRows,
                                        columns: {
                                            base: {
                                                ...baseDefault,
                                                content: baseModelId
                                                    ? getUpgradeDhwRowLabels({
                                                          domesticHotWater: baseModelData?.domesticHotWater || {},
                                                          heatingCooling: baseModelData?.heatingCooling || {},
                                                          rowId: obj.id,
                                                      })?.label
                                                    : "-",
                                                className: `${classes.regularCell} ${classes.baseCell}`,
                                                // verticalAlign: "top",
                                            },
                                            ...upgradePackages.reduce((packagesObj, upgradePackage) => {
                                                const { id, upgrades = {}, upgradeSettings = {} } = upgradePackage;
                                                const isInRun = !!upgradePackagesInRun?.[id];
                                                const { label = "", upgradeIds = [] } = getUpgradeDhwRowLabels({
                                                    domesticHotWater: {},
                                                    heatingCooling: {},
                                                    rowId: obj.id,
                                                    upgradePackage: {
                                                        [id]: upgradePackage,
                                                    },
                                                }) || {};

                                                //Must get the heating system type so we know if we're dealing with a combo
                                                const upHeatingCooling =
                                                    Object.values(upgradePackage?.upgrades || {}).find(
                                                        (val) => val.upgradeType === "heatingCooling"
                                                    ) || {};

                                                const {
                                                    fields: { heating: { system: heatingSystem = {} } = {} } = {},
                                                } = upHeatingCooling || {};

                                                const initUpgradeData = {
                                                    fields: getBlankUpgradeTemplates(obj.id),
                                                };

                                                const toggleUpgrade = (upgradeId, whereToAddCode, isNew) =>
                                                    toggleUpgradeDrawer({
                                                        isOpen: true,
                                                        formName: "upgrades",
                                                        selectedPackage: id,
                                                        selectedUpgrade: upgradeId,
                                                        selectedUpgradeType: obj.id,
                                                        selectedUpgradeH2kType: obj.upgradeType,
                                                        selectedUpgradeTypeLabel: obj.content,
                                                        selectedUpgradeLabel: label || "None",
                                                        initUpgradeData,
                                                        associatedHeatingSystem: Object.keys(heatingSystem)[0] || "",
                                                        isNew: isNew,
                                                        upgradeWhereToAddCode: whereToAddCode,
                                                        canBeEditedFromCommunity: !isInRun,
                                                        resetState: () => {
                                                            setUpgradeWhereToAddCode({});
                                                            removeUpgradeFromCommunity(obj.id, upgradeId);
                                                        },
                                                        modelsDefaultCodes,
                                                    });

                                                const upgradesObj =
                                                    upgradeIds.length === 0
                                                        ? buildUpgradeCell({
                                                              empty: true,
                                                              toggleUpgrade,
                                                              upgrades: [
                                                                  {
                                                                      displayLabel: label,
                                                                      upgradeType: obj.id,
                                                                  },
                                                              ],
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                              canBeAddedFromCommunity: !isInRun,
                                                          })
                                                        : buildUpgradeCell({
                                                              empty: false,
                                                              toggleUpgrade,
                                                              componentsRemaining: false,
                                                              upgrades: upgradeIds.map((upgradeId) => {
                                                                  return {
                                                                      displayLabel:
                                                                          upgrades[upgradeId]?.label?.[obj.id] || label,
                                                                      upgradeType: obj.id,
                                                                      upgradeId,
                                                                      cost:
                                                                          upgrades[upgradeId]?.cost?.[obj.id]
                                                                              ?.communityTotals?.[baseModelId] || 0,
                                                                      onChangeCost: (cost) =>
                                                                          handleUpgradeCostChange({
                                                                              packageId: id,
                                                                              upgradeId,
                                                                              upgradeType: obj.id,
                                                                              cost,
                                                                          }),
                                                                  };
                                                              }),
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                          });

                                                return {
                                                    ...packagesObj,
                                                    [id]: upgradesObj,
                                                };
                                            }, {}),
                                        },
                                        className: `${classes.regularCell} ${classes.freezeCell} ${classes.flexCell}`,
                                    });
                                }),
                                {
                                    house: {
                                        id: "other",
                                        value: "other",
                                        content: <strong>OTHER</strong>,
                                        className: `${classes.dividerCell} ${classes.freezeCell}`,
                                    },
                                    ...getEmptyDividerCells(upgradePackages, showCost),
                                },
                                ...otherRowHeadings.filter(rowFilter).map((obj) =>
                                    buildRow({
                                        ...obj,
                                        change,
                                        hideRows,
                                        hiddenRows,
                                        columns: {
                                            base: {
                                                ...baseDefault,
                                                content: baseModelId
                                                    ? getUpgradeOtherRowLabels({
                                                          generation: baseModelData?.generation || {},
                                                          program: baseModelData?.program || {},
                                                          baseLoads: baseModelData?.baseLoads || {},
                                                          rowId: obj.id,
                                                      })?.label
                                                    : "-",
                                                className: `${classes.regularCell} ${classes.baseCell}`,
                                                // verticalAlign: "top",
                                            },
                                            ...upgradePackages.reduce((packagesObj, upgradePackage) => {
                                                const { id, upgrades = {}, upgradeSettings = {} } = upgradePackage;
                                                const isInRun = !!upgradePackagesInRun?.[id];
                                                const { label = "", upgradeIds = [] } = getUpgradeOtherRowLabels({
                                                    generation: {},
                                                    program: {},
                                                    baseLoads: {},
                                                    rowId: obj.id,
                                                    upgradePackage: {
                                                        [id]: upgradePackage,
                                                    },
                                                }) || {};

                                                const initUpgradeData = {
                                                    fields: getBlankUpgradeTemplates(obj.id),
                                                };

                                                const toggleUpgrade = (upgradeId, whereToAddCode, isNew) =>
                                                    toggleUpgradeDrawer({
                                                        isOpen: true,
                                                        formName: "upgrades",
                                                        selectedPackage: id,
                                                        selectedUpgrade: upgradeId,
                                                        selectedUpgradeType: obj.id, // waterUsage, electricalUsage, baseloads, etc.
                                                        selectedUpgradeH2kType: obj.upgradeType, // generation, baseloads
                                                        selectedUpgradeTypeLabel: obj.content,
                                                        selectedUpgradeLabel: label || "None",
                                                        initUpgradeData,
                                                        isNew: isNew,
                                                        upgradeWhereToAddCode: whereToAddCode,
                                                        canBeEditedFromCommunity: !isInRun,
                                                        resetState: () => {
                                                            setUpgradeWhereToAddCode({});
                                                            removeUpgradeFromCommunity(obj.upgradeType, upgradeId);
                                                        },
                                                        modelsDefaultCodes,
                                                    });

                                                const upgradesObj =
                                                    upgradeIds.length === 0
                                                        ? buildUpgradeCell({
                                                              empty: true,
                                                              toggleUpgrade,
                                                              upgrades: [
                                                                  {
                                                                      displayLabel: label,
                                                                      upgradeType: obj.id,
                                                                  },
                                                              ],
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                              canBeAddedFromCommunity: !isInRun,
                                                          })
                                                        : buildUpgradeCell({
                                                              empty: false,
                                                              toggleUpgrade,
                                                              componentsRemaining: false,
                                                              upgrades: upgradeIds.map((upgradeId) => {
                                                                  return {
                                                                      displayLabel:
                                                                          upgrades[upgradeId]?.label?.[obj.id] || label,
                                                                      upgradeType: obj.id,
                                                                      upgradeId,
                                                                      cost:
                                                                          upgrades[upgradeId]?.cost?.[obj.id]
                                                                              ?.communityTotals?.[baseModelId] || 0,
                                                                      onChangeCost: (cost) =>
                                                                          handleUpgradeCostChange({
                                                                              packageId: id,
                                                                              upgradeId,
                                                                              upgradeType: obj.id,
                                                                              cost,
                                                                          }),
                                                                  };
                                                              }),
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                          });

                                                return {
                                                    ...packagesObj,
                                                    [id]: upgradesObj,
                                                };
                                            }, {}),
                                        },
                                        className: `${classes.regularCell} ${classes.freezeCell} ${classes.flexCell}`,
                                    })
                                ),
                                ...communityFinancingRowHeadings.filter(rowFilter).map((obj) =>
                                    buildRow({
                                        ...obj,
                                        change,
                                        hideRows,
                                        hiddenRows,
                                        columns: {
                                            base: {
                                                ...baseDefault,
                                                content: baseModelId
                                                    ? getUpgradeFinancingRowsLabels({
                                                          rowId: obj.id,
                                                      })?.label
                                                    : "-",
                                                className: `${classes.regularCell} ${classes.baseCell}`,
                                                // verticalAlign: "top",
                                            },
                                            ...upgradePackages.reduce((packagesObj, upgradePackage) => {
                                                const { id, upgrades = {}, upgradeSettings = {} } = upgradePackage;
                                                const { label = "", upgradeIds = [] } = getUpgradeFinancingRowsLabels({
                                                    rowId: obj.id,
                                                    upgradePackage: {
                                                        [id]: upgradePackage,
                                                    },
                                                }) || {};

                                                const toggleUpgrade = () => {};

                                                const upgradesObj =
                                                    upgradeIds.length === 0
                                                        ? buildUpgradeCell({
                                                              empty: true,
                                                              toggleUpgrade,
                                                              upgrades: [
                                                                  {
                                                                      displayLabel: label,
                                                                      upgradeType: obj.id,
                                                                  },
                                                              ],
                                                              showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                          })
                                                        : buildUpgradeCell({
                                                              empty: false,
                                                              toggleUpgrade,
                                                              componentsRemaining: false,
                                                              upgrades: upgradeIds.map((upgradeId) => {
                                                                  return {
                                                                      displayLabel:
                                                                          dollarFormatter.format(
                                                                              capitalCosts[baseModelId]?.[id] || 0
                                                                          ) ||
                                                                          label ||
                                                                          "$0.00",
                                                                      upgradeType: obj.id,
                                                                      upgradeId,
                                                                  };
                                                              }),
                                                              //   showCost,
                                                              setUpgradeWhereToAddCode,
                                                              filteredUpgrades,
                                                          });

                                                return {
                                                    ...packagesObj,
                                                    [id]: upgradesObj,
                                                };
                                            }, {}),
                                        },
                                        className: `${classes.regularCell} ${classes.freezeCell} ${classes.flexCell}`,
                                    })
                                ),
                            ]}
                        />

                        <UpgradeDrawer
                            toggleOpen={toggleUpgradeDrawer}
                            isCommunity={true}
                            showCost={showCost}
                            toggleShowCost={toggleShowCost}
                            {...upgradeOpen}
                        />
                        <PackageEditDrawer toggleOpen={togglePackageEdit} isCommunity={true} {...packageEditOpen} />
                        <CodeDrawer upgradeWhereToAddCode={upgradeWhereToAddCode} />
                    </>
                ) : (
                    <>
                        <Loading className={sharedClasses.spinner} />
                    </>
                )}
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({
    modelDir: selectCommunityModelDir,
    modelData: selectCommunityModelData,
    communityLoading: selectCommunityLoading,
    capitalCosts: selectCapitalCosts,
    upgradePackages: selectUpgradePackagesSortedArray,
    upgradePackagesInRun: selectUpgradePackagesInRun,
});

const mapDispatchToProps = (dispatch) => ({
    fetchUpgradePackage: (uid, packageId, isFromOrg, orgId) =>
        dispatch(fetchUpgradePackage(uid, packageId, isFromOrg, orgId)),
    removeUpgradeFromCommunity: (sectionType, upgradeId) =>
        dispatch(removeUpgradeFromCommunity(sectionType, upgradeId)),
    setCommBaseModelId: (baseModelId) => dispatch(setCommBaseModelId(baseModelId)),
    updateCommUpgradePackage: ({ packageId, packageUpdates }) =>
        dispatch(updateCommUpgradePackage({ packageId, packageUpdates })),
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(CommunitiesUpgradesTable);
